/**
 * ============================================================================
 * COOPER & FRENCH VARIANT-SPECIFIC INVENTORY TRACKER
 * ============================================================================
 * 
 * Dynamically updates inventory display when customers select different
 * product variants (scents, sizes, etc.)
 * 
 * INSTALLATION:
 * 1. Save as: assets/cf-inventory.js
 * 2. Add to theme.liquid before </body>:
 *    {{ 'cf-inventory.js' | asset_url | script_tag }}
 * 
 * REQUIREMENTS:
 * - Product JSON must be available on page
 * - cf-inventory-display.liquid snippet must be rendered
 * - cf-inventory.css must be loaded
 * 
 * FEATURES:
 * - Real-time inventory updates on variant selection
 * - Smooth transitions between states
 * - Multiple variant selector support
 * - Restock notification handling
 * - Analytics tracking (optional)
 * 
 * ============================================================================
 */

(function() {
  'use strict';
  
  // ============================================================================
  // CONFIGURATION
  // ============================================================================
  
  const CONFIG = {
    // Selectors for DOM elements
    selectors: {
      inventoryContainer: '[data-inventory-container]',
      variantSelector: '[name="id"]',
      productJson: '[data-product-json]',
      optionSelectors: '[name^="options"]',
      thresholdsData: '[data-cf-inventory-thresholds]',
      restockForm: '[data-restock-form]'
    },
    
    // Default thresholds (can be overridden by Liquid data)
    thresholds: {
      critical: 5,
      low: 10,
      medium: 20
    },
    
    // Animation timings
    animation: {
      fadeOut: 150,
      fadeIn: 150
    },
    
    // Enable debug logging
    debug: false
  };
  
  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  
  /**
   * Log debug messages if debug mode is enabled
   */
  function debug(...args) {
    if (CONFIG.debug) {
      console.log('[CF Inventory]', ...args);
    }
  }
  
  /**
   * Get inventory level category based on quantity and thresholds
   */
  function getInventoryLevel(quantity, thresholds) {
    if (quantity <= 0) return 'out';
    if (quantity <= thresholds.critical) return 'critical';
    if (quantity <= thresholds.low) return 'low';
    if (quantity <= thresholds.medium) return 'medium';
    return 'available';
  }
  
  /**
   * Get SVG icon for specific inventory level
   */
  function getIcon(level) {
    const icons = {
      critical: `<svg class="cf-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M8 1L1 15h14L8 1z" stroke="currentColor" stroke-width="2"/>
        <path d="M8 6v4M8 11v1" stroke="currentColor" stroke-width="2"/>
      </svg>`,
      
      low: `<svg class="cf-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="2"/>
        <path d="M8 4v5M8 11v1" stroke="currentColor" stroke-width="2"/>
      </svg>`,
      
      medium: `<svg class="cf-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M13 5L6 12L3 9" stroke="currentColor" stroke-width="2"/>
      </svg>`,
      
      available: `<svg class="cf-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M13 5L6 12L3 9" stroke="currentColor" stroke-width="2"/>
      </svg>`,
      
      out: `<svg class="cf-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="2"/>
      </svg>`
    };
    
    return icons[level] || icons.available;
  }
  
  /**
   * Get inventory message text
   */
  function getMessage(quantity, level) {
    const messages = {
      critical: `<strong>Only ${quantity} left!</strong> Order soon`,
      low: `Only <strong>${quantity} in stock</strong>`,
      medium: `<strong>${quantity}</strong> available`,
      available: 'In Stock & Ready to Ship',
      out: 'Currently Unavailable'
    };
    
    return messages[level] || messages.available;
  }
  
  /**
   * Generate complete inventory badge HTML
   */
  function generateInventoryHTML(quantity, level, thresholds) {
    return `
      <div class="cf-inventory-badge cf-${level}" data-inventory-level="${level}">
        ${getIcon(level)}
        <span class="cf-inventory-text">
          ${getMessage(quantity, level)}
        </span>
      </div>
    `;
  }
  
  // ============================================================================
  // INVENTORY UPDATE LOGIC
  // ============================================================================
  
  /**
   * Update inventory display for selected variant
   */
  function updateInventoryDisplay(variantId, productData, thresholds) {
    const container = document.querySelector(CONFIG.selectors.inventoryContainer);
    if (!container) {
      debug('Inventory container not found');
      return;
    }
    
    // Find the selected variant in product data
    const variant = productData.variants.find(v => v.id === parseInt(variantId));
    
    if (!variant) {
      debug('Variant not found:', variantId);
      return;
    }
    
    // Check if inventory tracking is enabled for this variant
    if (variant.inventory_management !== 'shopify') {
      debug('Inventory not tracked for variant:', variantId);
      container.innerHTML = '';
      return;
    }
    
    const quantity = variant.inventory_quantity || 0;
    const level = getInventoryLevel(quantity, thresholds);
    
    debug('Updating inventory:', {
      variantId,
      quantity,
      level,
      variantTitle: variant.title
    });
    
    // Smooth fade transition
    container.style.opacity = '0';
    
    setTimeout(() => {
      container.innerHTML = generateInventoryHTML(quantity, level, thresholds);
      container.style.opacity = '1';
      
      // Trigger custom event for analytics or other integrations
      const event = new CustomEvent('cf:inventoryUpdated', {
        detail: {
          variantId,
          quantity,
          level,
          productId: productData.id
        }
      });
      document.dispatchEvent(event);
      
    }, CONFIG.animation.fadeOut);
  }
  
  // ============================================================================
  // RESTOCK NOTIFICATION HANDLING
  // ============================================================================
  
  /**
   * Handle restock notification form submission
   */
  function handleRestockFormSubmit(event) {
    event.preventDefault();
    
    const form = event.target;
    const email = form.querySelector('input[name="email"]').value;
    const variantId = form.getAttribute('data-variant-id');
    const submitButton = form.querySelector('button[type="submit"]');
    const successMessage = form.nextElementSibling;
    
    // Disable form during submission
    submitButton.disabled = true;
    submitButton.textContent = 'Submitting...';
    
    debug('Restock notification requested:', { email, variantId });
    
    // Send to your restock notification service
    // This is a placeholder - implement your actual endpoint
    fetch('/apps/restock-notifications/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        email: email,
        variant_id: variantId,
        source: 'product_page'
      })
    })
    .then(response => response.json())
    .then(data => {
      debug('Restock notification success:', data);
      
      // Show success message
      form.style.display = 'none';
      if (successMessage) {
        successMessage.style.display = 'block';
      }
      
      // Track the subscription in analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'restock_notification_signup', {
          'variant_id': variantId,
          'email': email
        });
      }
    })
    .catch(error => {
      console.error('[CF Inventory] Restock notification error:', error);
      
      // Re-enable form
      submitButton.disabled = false;
      submitButton.textContent = 'Notify Me';
      
      // Show error message
      alert('Something went wrong. Please try again or contact us directly.');
    });
  }
  
  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  
  /**
   * Initialize inventory tracking system
   */
  function init() {
    debug('Initializing inventory tracker...');
    
    // Get product data from JSON
    const productJsonElement = document.querySelector(CONFIG.selectors.productJson);
    if (!productJsonElement) {
      debug('Product JSON not found - inventory tracking disabled');
      return;
    }
    
    let productData;
    try {
      productData = JSON.parse(productJsonElement.textContent);
      debug('Product data loaded:', productData.title);
    } catch (e) {
      console.error('[CF Inventory] Failed to parse product JSON:', e);
      return;
    }
    
    // Load custom thresholds if provided
    let thresholds = { ...CONFIG.thresholds };
    const thresholdsElement = document.querySelector(CONFIG.selectors.thresholdsData);
    if (thresholdsElement) {
      try {
        const customThresholds = JSON.parse(thresholdsElement.textContent);
        thresholds = { ...thresholds, ...customThresholds };
        debug('Custom thresholds loaded:', thresholds);
      } catch (e) {
        debug('Failed to parse custom thresholds, using defaults');
      }
    }
    
    // Find variant selector
    const variantSelector = document.querySelector(CONFIG.selectors.variantSelector);
    if (!variantSelector) {
      debug('Variant selector not found - single variant product or theme incompatibility');
      return;
    }
    
    // Add smooth transition to container
    const container = document.querySelector(CONFIG.selectors.inventoryContainer);
    if (container) {
      container.style.transition = `opacity ${CONFIG.animation.fadeOut}ms ease-in-out`;
    }
    
    // AGGRESSIVE MONITORING APPROACH - Poll for changes since events aren't firing
    let lastVariantId = variantSelector.value;
    debug('Starting variant monitor with initial variant:', lastVariantId);
    
    // Method 1: MutationObserver - watches for DOM changes to the select element
    const observer = new MutationObserver(function(mutations) {
      const currentVariantId = variantSelector.value;
      if (currentVariantId !== lastVariantId) {
        debug('MutationObserver detected variant change:', lastVariantId, '->', currentVariantId);
        lastVariantId = currentVariantId;
        updateInventoryDisplay(currentVariantId, productData, thresholds);
      }
    });
    
    observer.observe(variantSelector, {
      attributes: true,
      attributeFilter: ['value'],
      childList: true,
      subtree: true
    });
    
    // Method 2: Polling - check every 500ms for variant changes
    setInterval(function() {
      const currentVariantId = variantSelector.value;
      if (currentVariantId !== lastVariantId) {
        debug('Polling detected variant change:', lastVariantId, '->', currentVariantId);
        lastVariantId = currentVariantId;
        updateInventoryDisplay(currentVariantId, productData, thresholds);
      }
    }, 500);
    
    // Method 3: Hook into Shopify's OptionSelectors callback if available
    if (window.selectCallback) {
      debug('Detected Shopify.OptionSelectors - wrapping selectCallback');
      const originalCallback = window.selectCallback;
      window.selectCallback = function(variant, selector) {
        // Call original callback first
        originalCallback(variant, selector);
        
        // Then update our inventory display
        if (variant && variant.id) {
          debug('OptionSelectors callback fired with variant:', variant.id);
          lastVariantId = variant.id.toString();
          updateInventoryDisplay(variant.id, productData, thresholds);
        }
      };
    }
    
    // Method 4: Try to wrap timber.productPage if it exists
    setTimeout(function() {
      if (window.timber && window.timber.productPage) {
        debug('Detected timber.productPage - will monitor');
      }
    }, 1000);
    
    // Handle restock notification forms
    document.addEventListener('submit', function(e) {
      if (e.target.matches(CONFIG.selectors.restockForm)) {
        handleRestockFormSubmit(e);
      }
    });
    
    // Initialize with current variant
    const initialVariantId = variantSelector.value;
    if (initialVariantId) {
      debug('Initializing with variant:', initialVariantId);
      updateInventoryDisplay(initialVariantId, productData, thresholds);
    }
    
    debug('Inventory tracker initialized successfully');
  }
  
  // ============================================================================
  // AUTO-INITIALIZATION
  // ============================================================================
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded
    init();
  }
  
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  
  // Expose public methods for manual control or debugging
  window.CFInventory = {
    version: '1.0.0',
    init: init,
    updateDisplay: updateInventoryDisplay,
    config: CONFIG,
    
    // Enable debug mode
    enableDebug: function() {
      CONFIG.debug = true;
      debug('Debug mode enabled');
    },
    
    // Disable debug mode
    disableDebug: function() {
      CONFIG.debug = false;
    }
  };
  
  debug('Cooper & French Inventory module loaded');
  
})();

/**
 * ============================================================================
 * INTEGRATION EXAMPLES
 * ============================================================================
 * 
 * // Manually reinitialize after AJAX product load
 * window.CFInventory.init();
 * 
 * // Enable debug logging
 * window.CFInventory.enableDebug();
 * 
 * // Listen for inventory updates
 * document.addEventListener('cf:inventoryUpdated', function(e) {
 *   console.log('Inventory updated:', e.detail);
 *   // Your custom logic here
 * });
 * 
 * // Manually update inventory display
 * window.CFInventory.updateDisplay(
 *   variantId, 
 *   productData, 
 *   { critical: 5, low: 10, medium: 20 }
 * );
 * 
 * ============================================================================
 */